import { Link, useParams } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { useHelpCategories, useHelpArticles } from "@/hooks/useHelpCenter";
import { FileText, ChevronRight, BookOpen } from "lucide-react";

export function HelpCategoryPage() {
  const { categorySlug } = useParams<{ categorySlug: string }>();
  const { data: categories } = useHelpCategories();
  const { data: articles, isLoading } = useHelpArticles(categorySlug);

  const category = categories?.find((c) => c.slug === categorySlug);

  // Filter articles that belong to this category
  const categoryArticles = articles?.filter(
    (article) => (article as any).help_categories?.slug === categorySlug
  );

  return (
    <div className="container max-w-4xl mx-auto py-8 px-4">
      <Breadcrumb className="mb-6">
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink asChild>
              <Link to="/help">Help Center</Link>
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbPage>{category?.name || categorySlug}</BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>

      <div className="mb-8">
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <BookOpen className="h-6 w-6" />
          {category?.name || categorySlug}
        </h1>
        {category?.description && (
          <p className="text-muted-foreground mt-2">{category.description}</p>
        )}
      </div>

      {isLoading ? (
        <div className="space-y-3">
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="h-16 w-full" />
          ))}
        </div>
      ) : categoryArticles?.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            <FileText className="h-12 w-12 mx-auto mb-3 opacity-50" />
            <p>No articles in this category yet</p>
            <Link
              to="/help"
              className="text-primary hover:underline mt-2 inline-block"
            >
              Return to Help Center
            </Link>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {categoryArticles?.map((article) => (
            <Link
              key={article.id}
              to={`/help/${categorySlug}/${article.slug}`}
              className="block"
            >
              <Card className="hover:bg-accent transition-colors">
                <CardContent className="py-4 flex items-center justify-between">
                  <div className="flex items-start gap-3">
                    <FileText className="h-5 w-5 text-muted-foreground mt-0.5" />
                    <div>
                      <h3 className="font-medium">{article.title}</h3>
                      <p className="text-sm text-muted-foreground mt-1">
                        Updated{" "}
                        {new Date(article.updated_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  <ChevronRight className="h-5 w-5 text-muted-foreground shrink-0" />
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
